import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './AgeVerified.css';
import {
  ArrowForwardRounded,
  CheckCircleOutlineRounded,
  HighlightOffRounded,
  KeyOutlined,
  VerifiedUserOutlined
} from '@mui/icons-material';
import { useIdentification } from './IdentificationContext';
import config from './config';
import {
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar
} from './ui/EnterpriseLayout';

// Helper function to convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const API_BASE_URL = config.API_BASE_URL;

function AgeVerified() {
  const navigate = useNavigate();
  const location = useLocation();
  const { identificationId } = useIdentification();
  const ageEstimationResult = location.state?.ageEstimationResult;
  const faceScanResult = location.state?.faceScanResult;
  const isAgeEstimation = location.state?.isAgeEstimation;
  const isIdVerification = location.state?.isIdVerification;
  const isDigitalId = location.state?.isDigitalId;
  const digitalIdVerified = location.state?.isVerified;
  
  // Passkey-related state
  const [verificationStatus, setVerificationStatus] = useState('PENDING'); // 'PENDING' or 'VERIFIED'
  const [isPasskeyRegistered, setIsPasskeyRegistered] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Determine verification status
  // For age estimation: only check over18
  // For ID verification: need both over18 AND faceMatch
  // For digital ID: use the provided isVerified status
  let isVerified = false;
  
  if (isDigitalId) {
    isVerified = digitalIdVerified === true;
  } else if (isAgeEstimation && ageEstimationResult) {
    isVerified = ageEstimationResult.over18 === true;
  } else if (isIdVerification && faceScanResult) {
    isVerified = faceScanResult.over18 === true && faceScanResult.faceMatch === true;
  } else {
    // Default to verified if no result data (backwards compatibility)
    isVerified = true;
  }
  
  const [progress, setProgress] = useState(0);
  const [documentDeleted, setDocumentDeleted] = useState(false);
  const [faceDeleted, setFaceDeleted] = useState(false);

  // Finalize verification (Step 1)
  const finalizeVerification = async () => {
    if (!identificationId) {
      setError('No identification ID available');
      return;
    }

    setIsFinalizing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/identifications/${identificationId}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to finalize verification: ${response.statusText}`);
      }

      const data = await response.json();
      setVerificationStatus(data.verificationStatus || 'VERIFIED');
      setSuccessMessage('Verification finalized successfully! You can now register a passkey.');
      
      // Store age token in localStorage for future use
      if (data.ageToken) {
        localStorage.setItem('ageToken', data.ageToken);
        if (data.ageTokenExpiresAt) {
          localStorage.setItem('ageTokenExpiresAt', data.ageTokenExpiresAt);
        }
      }
    } catch (err) {
      console.error('Error finalizing verification:', err);
      setError(err.message || 'Failed to finalize verification. Please try again.');
    } finally {
      setIsFinalizing(false);
    }
  };

  // Register passkey (Step 2)
  const registerPasskey = async () => {
    if (!identificationId) {
      setError('No identification ID available');
      return;
    }

    if (verificationStatus !== 'VERIFIED') {
      setError('Please finalize verification first');
      return;
    }

    // Check if WebAuthn is supported
    if (!navigator.credentials || !navigator.credentials.create) {
      setError('WebAuthn is not supported in this browser. Please use a modern browser.');
      return;
    }

    setIsRegisteringPasskey(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Generate a random challenge (you can also get this from the server)
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Create WebAuthn credential
      const publicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: 'Age Verification',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(identificationId),
          name: identificationId,
          displayName: 'Age Verified User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          // Don't restrict to 'platform' - allow both platform and cross-platform passkeys
          // This enables synced passkeys (e.g., from Google Password Manager) to work
          // userVerification: 'required' allows any authenticator type
          userVerification: 'required',
          requireResidentKey: true, // Required for synced/cross-platform passkeys
        },
        timeout: 60000,
        attestation: 'direct',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      if (!credential) {
        throw new Error('Failed to create passkey');
      }

      // Extract credential data and convert to Base64
      const credentialId = arrayBufferToBase64(credential.rawId);
      
      // Try to get public key - it may not exist as a method, so we'll handle it gracefully
      let publicKey = '';
      try {
        if (credential.response.getPublicKey) {
          publicKey = arrayBufferToBase64(credential.response.getPublicKey());
        } else {
          // Public key is embedded in attestationObject, API should extract it
          // For now, send empty string - the API can extract from attestationObject
          console.warn('getPublicKey() method not available, API will extract from attestationObject');
        }
      } catch (e) {
        console.warn('Could not extract public key:', e);
        // API should be able to extract it from attestationObject
      }
      
      const attestationObject = arrayBufferToBase64(credential.response.attestationObject);
      const clientDataJSON = arrayBufferToBase64(credential.response.clientDataJSON);

      // Send to server
      const response = await fetch(`${API_BASE_URL}/identifications/${identificationId}/passkey/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId,
          publicKey,
          attestationObject,
          clientDataJSON,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to register passkey: ${response.statusText}`);
      }

      await response.json();
      setIsPasskeyRegistered(true);
      setSuccessMessage('Passkey registered successfully! You can now use it to authenticate in the future.');
      
      // Store credential ID for future authentication
      localStorage.setItem('passkeyCredentialId', credentialId);
    } catch (err) {
      console.error('Error registering passkey:', err);
      // Handle user cancellation gracefully
      if (err.name === 'NotAllowedError' || err.message.includes('cancel')) {
        setError('Passkey registration was cancelled. You can try again later.');
      } else {
        setError(err.message || 'Failed to register passkey. Please try again.');
      }
    } finally {
      setIsRegisteringPasskey(false);
    }
  };

  useEffect(() => {
    // Animate progress bar
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    // Show document deleted after 1 second
    const documentTimer = setTimeout(() => {
      setDocumentDeleted(true);
    }, 1000);

    // Show face deleted after 2 seconds
    const faceTimer = setTimeout(() => {
      setFaceDeleted(true);
    }, 2000);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(documentTimer);
      clearTimeout(faceTimer);
    };
  }, []);

  // Only show deletion progress if verified or if no result data (default case)
  // Don't show for digital ID since no files are uploaded
  const showDeletionProgress = !isDigitalId && (isVerified || (!isAgeEstimation && !isIdVerification));

  return (
    <EnterpriseShell>
      <EnterpriseTopBar right={<span className="vt-chip vt-chip--subtle">Results</span>} />

      <EnterpriseHero
        title={!isVerified ? 'Age not verified' : 'Age verified'}
        subtitle={
          !isVerified
            ? 'You did not meet the required age threshold.'
            : isDigitalId
            ? 'Your age has been verified.'
            : 'Your age has been verified. You can optionally save this verification as a passkey (Veritage Key).'
        }
      />

      <EnterpriseGrid columns={1}>
        <EnterpriseCard>
          <div className="vt-cardHeader">
            <div className="vt-iconTile" aria-hidden="true">
              {isVerified ? (
                <CheckCircleOutlineRounded className="vt-iconTileIcon" />
              ) : (
                <HighlightOffRounded className="vt-iconTileIcon" />
              )}
            </div>
            <div>
              <h2 className="vt-cardTitle">{isVerified ? 'Verified' : 'Not verified'}</h2>
              <p className="vt-cardDescription">
                {isVerified
                  ? isDigitalId
                    ? 'Verification complete. You may proceed.'
                    : 'Verification complete. You may proceed or register a passkey for reuse.'
                  : 'Try another method or retry where available.'}
              </p>
            </div>
          </div>

        {/* Age estimation details */}
        {isAgeEstimation && ageEstimationResult && (
          <div className="age-estimation-details">
            {isVerified ? (
              <p className="age-estimation-text">
                Estimated age: {ageEstimationResult.ageLow}-{ageEstimationResult.ageHigh} years
              </p>
            ) : (
              <p className="age-estimation-text error-text">
                Estimated age: {ageEstimationResult.ageLow}-{ageEstimationResult.ageHigh} years. You must be 18 or over.
              </p>
            )}
          </div>
        )}

        {/* ID verification details */}
        {isIdVerification && faceScanResult && !isVerified && (
          <div className="age-estimation-details">
            {!faceScanResult.over18 && (
              <p className="age-estimation-text error-text">
                Age verification failed. You must be 18 or over.
              </p>
            )}
            {!faceScanResult.faceMatch && faceScanResult.over18 && (
              <p className="age-estimation-text error-text">
                Face match failed. The face scan does not match the ID document.
              </p>
            )}
            {!faceScanResult.over18 && !faceScanResult.faceMatch && (
              <p className="age-estimation-text error-text">
                Verification failed. You must be 18 or over and your face must match the ID document.
              </p>
            )}
          </div>
        )}

        {/* Digital ID details */}
        {isDigitalId && !isVerified && (
          <div className="age-estimation-details">
            <p className="age-estimation-text error-text">
              Age verification failed. You must be 18 or over.
            </p>
          </div>
        )}

        {/* Progress section - only show if verified or default case */}
        {showDeletionProgress && (
          <div className="deletion-progress-section">
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            
            <div className="deletion-items">
              {isIdVerification && (
                <div className={`deletion-item ${documentDeleted ? 'completed' : ''}`}>
                  {documentDeleted && <CheckCircleOutlineRounded className="deletion-checkmark" />}
                  <span className="deletion-text">Document scan deleted</span>
                </div>
              )}
              
              <div className={`deletion-item ${faceDeleted ? 'completed' : ''}`}>
                {faceDeleted && <CheckCircleOutlineRounded className="deletion-checkmark" />}
                <span className="deletion-text">Face scan deleted</span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {isVerified && !isDigitalId && (
          <p className="vt-note">
            {verificationStatus === 'PENDING' 
              ? 'Finalize your verification to save it in a secure, anonymous passkey.'
              : isPasskeyRegistered
              ? 'Your age verification is saved in a secure passkey. You can use it to authenticate in the future.'
              : 'To reuse your age verification, save it in a secure, anonymous passkey.'}
          </p>
        )}

          {isVerified && (
            <div className="vt-actions">
              {verificationStatus === 'PENDING' && !isDigitalId && (
                <EnterpriseButton variant="primary" onClick={finalizeVerification} disabled={isFinalizing}>
                  <span>{isFinalizing ? 'Finalizing…' : 'Finalize verification'}</span>
                  <VerifiedUserOutlined className="vt-btnIcon" />
                </EnterpriseButton>
              )}

              {verificationStatus === 'VERIFIED' && !isPasskeyRegistered && !isDigitalId && (
                <EnterpriseButton variant="primary" onClick={registerPasskey} disabled={isRegisteringPasskey}>
                  <span>{isRegisteringPasskey ? 'Registering…' : 'Register passkey'}</span>
                  <KeyOutlined className="vt-btnIcon" />
                </EnterpriseButton>
              )}

              <EnterpriseButton variant="secondary" type="button" onClick={() => navigate('/')}>
                Back to options
                <ArrowForwardRounded className="vt-btnIcon" />
              </EnterpriseButton>
            </div>
          )}
        
        {!isVerified && (
            <div className="vt-actions">
              {isAgeEstimation && (
                <EnterpriseButton variant="primary" onClick={() => navigate('/select-id-type')}>
                  Upload ID document
                  <ArrowForwardRounded className="vt-btnIcon" />
                </EnterpriseButton>
              )}
              <EnterpriseButton variant="secondary" type="button" onClick={() => navigate('/')}>
                Return to options
              </EnterpriseButton>
            </div>
        )}
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default AgeVerified;
